/*
 * @providesModule DepositSuccessOverlayManager
 * 
 * Global overlay manager for DepositSuccessPopup that renders at the root level
 * to ensure it overlays the entire SDK and is not affected by navigation changes.
 */

import React from 'react';
import {Modal, StyleSheet, View, Platform} from 'react-native';
import DepositSuccessPopup from 'DepositSuccessPopup';
import {DepositSuccessModalDeposit} from 'DepositSuccessModal';

type DepositSuccessOverlayState = {
  visible: boolean;
  deposit: DepositSuccessModalDeposit | null;
  onDismiss: (() => void) | null;
  onCancelPress: (() => void) | null;
};

type PendingShowCall = {
  deposit: DepositSuccessModalDeposit;
  onDismiss: () => void;
  onCancelPress?: () => void;
};

// Persist state outside component to survive remounts
let persistentState: DepositSuccessOverlayState = {
  visible: false,
  deposit: null,
  onDismiss: null,
  onCancelPress: null,
};

class DepositSuccessOverlayManager extends React.Component<{}, DepositSuccessOverlayState> {
  private static instance: DepositSuccessOverlayManager | null = null;
  private static pendingShowCall: PendingShowCall | null = null;

  constructor(props: {}) {
    super(props);
    // Initialize state from persistent state
    this.state = {...persistentState};
    DepositSuccessOverlayManager.instance = this;
    console.debug('DepositSuccessOverlayManager: Instance created and registered', {
      persistentStateVisible: persistentState.visible,
      hasPersistentDeposit: !!persistentState.deposit,
    });

    // Process any pending show call or restore persistent state
    if (DepositSuccessOverlayManager.pendingShowCall) {
      const pending = DepositSuccessOverlayManager.pendingShowCall;
      DepositSuccessOverlayManager.pendingShowCall = null;
      // Use setTimeout to ensure component is fully mounted
      setTimeout(() => {
        this.setState({
          visible: true,
          deposit: pending.deposit,
          onDismiss: pending.onDismiss,
          onCancelPress: pending.onCancelPress || null,
        });
        // Update persistent state
        persistentState = {
          visible: true,
          deposit: pending.deposit,
          onDismiss: pending.onDismiss,
          onCancelPress: pending.onCancelPress || null,
        };
        console.debug('DepositSuccessOverlayManager: Processed pending show call');
      }, 0);
    } else if (persistentState.visible && persistentState.deposit) {
      // Restore persistent state if component was remounted
      console.debug('DepositSuccessOverlayManager: Restoring persistent state after remount');
      setTimeout(() => {
        this.setState({...persistentState});
      }, 0);
    }
  }

  componentDidMount() {
    console.debug('DepositSuccessOverlayManager: Component mounted');
  }

  static show(
    deposit: DepositSuccessModalDeposit,
    onDismiss: () => void,
    onCancelPress?: () => void
  ) {
    console.debug('DepositSuccessOverlayManager.show called', {
      hasInstance: !!DepositSuccessOverlayManager.instance,
      deposit,
    });

    // Update persistent state first
    persistentState = {
      visible: true,
      deposit,
      onDismiss,
      onCancelPress: onCancelPress || null,
    };

    if (DepositSuccessOverlayManager.instance) {
      DepositSuccessOverlayManager.instance.setState({...persistentState});
      console.debug('DepositSuccessOverlayManager: State updated, modal should be visible');
    } else {
      // Instance not ready yet, queue the call
      console.warn(
        'DepositSuccessOverlayManager: Instance not initialized yet, queueing show call'
      );
      DepositSuccessOverlayManager.pendingShowCall = {
        deposit,
        onDismiss,
        onCancelPress,
      };
    }
  }

  static hide() {
    // Update persistent state first
    persistentState = {
      visible: false,
      deposit: null,
      onDismiss: null,
      onCancelPress: null,
    };

    if (DepositSuccessOverlayManager.instance) {
      DepositSuccessOverlayManager.instance.setState({...persistentState});
    }
  }

  static isVisible(): boolean {
    return DepositSuccessOverlayManager.instance?.state.visible || false;
  }

  private handleDismiss = () => {
    const {onDismiss} = this.state;
    // Update persistent state
    persistentState = {
      visible: false,
      deposit: null,
      onDismiss: null,
      onCancelPress: null,
    };
    this.setState({...persistentState});
    onDismiss?.();
  };

  private handleCancelPress = () => {
    const {onCancelPress, onDismiss} = this.state;
    // Update persistent state
    persistentState = {
      visible: false,
      deposit: null,
      onDismiss: null,
      onCancelPress: null,
    };
    this.setState({...persistentState});
    onCancelPress?.();
    // Also call onDismiss to ensure cleanup
    onDismiss?.();
  };

  render() {
    const {visible, deposit} = this.state;

    console.debug('DepositSuccessOverlayManager: render called', {
      visible,
      hasDeposit: !!deposit,
    });

    if (!visible || !deposit) {
      return null;
    }

    console.debug('DepositSuccessOverlayManager: Rendering Modal with DepositSuccessPopup');

    return (
      <Modal
        transparent={true}
        animationType="fade"
        visible={visible}
        onRequestClose={this.handleDismiss}
        // Ensure this modal is on top of everything
        {...(Platform.OS === 'ios' ? {presentationStyle: 'overFullScreen'} : {})}
      >
        <View style={styles.overlayContainer}>
          <DepositSuccessPopup
            deposit={deposit}
            onDismiss={this.handleDismiss}
            onCancelPress={this.state.onCancelPress ? this.handleCancelPress : undefined}
          />
        </View>
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  overlayContainer: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Highest z-index to ensure it's above all other content
    zIndex: 9999,
  },
});

export default DepositSuccessOverlayManager;

